/**
 * ReportCard — unified report card component for all portals.
 *
 * Layout:
 *  ┌──────────────────────────────────┐
 *  │  [COVER IMAGE — if coverUrl]     │  ← full-width, 172px, object-cover
 *  ├──────────────────────────────────┤
 *  │  [Category pill]  [badge]        │
 *  │  Title                           │
 *  │  Excerpt (2-line clamp)          │
 *  ├──────────────────────────────────┤
 *  │  💬 n  👁 n  📎 n   │  ● By  date  ⋮ │
 *  └──────────────────────────────────┘
 *
 * Props
 * ─────
 * post          — Firestore post object (+ _crossTeam, _shareType, _fromPortalLabel, _fromPortalColor)
 * cat           — { label, color, icon }  (resolved category config)
 * accentColor   — portal accent (fallback for cat-less styles)
 * isAdmin       — bool
 * user          — { uid, name, role }
 * onOpen        — fn(post)   open full detail
 * onEdit        — fn(post)
 * onDelete      — fn(post)
 * onToggleLike  — fn(post)   admin star/approve
 * onTogglePin   — fn(post)
 * onToggleStar  — fn(post, accentColor)
 * isStarred     — bool
 * pinLoading    — bool
 * onAllowEdit   — fn(post)
 * threads       — [] child posts
 * C             — theme color tokens { bg, panel, panel2, border, text, bright, dim, mono, sans, accent }
 */

import InternApprovalNote from '../InternBadge/InternApprovalNote';
import { useState } from 'react';
import { getFirestore, doc, updateDoc } from 'firebase/firestore';
import { getApp } from 'firebase/app';
import StarButton from '../ReportActions/StarButton';
import PinButton  from '../ReportActions/PinButton';
import WithinPortalSharePanel from '../ReportSharing/WithinPortalSharePanel';
import ExternalSharePanel     from '../ReportSharing/ExternalSharePanel';
import ShareViaChatModal     from '../ShareViaChatModal/ShareViaChatModal';
import { PORTAL_CARDS } from '../../constants/portalCards';
import ReportShareButton from '../ReportShare/ReportShareButton';
import { reportId } from '../../utils/reportId';

const relTime = ts => {
  if (!ts?.toDate) return ts ? new Date(ts).toLocaleDateString('en-IN', { day:'numeric', month:'short' }) : '—';
  const d = (Date.now() - ts.toDate().getTime()) / 1000;
  if (d < 60)     return 'just now';
  if (d < 3600)   return `${Math.floor(d/60)}m ago`;
  if (d < 86400)  return `${Math.floor(d/3600)}h ago`;
  if (d < 604800) return `${Math.floor(d/86400)}d ago`;
  return ts.toDate().toLocaleDateString('en-IN', { day:'numeric', month:'short' });
};

const initials = name => {
  if (!name) return '?';
  const p = name.trim().split(/\s+/);
  return p.length > 1 ? (p[0][0] + p[p.length-1][0]).toUpperCase() : name.slice(0,2).toUpperCase();
};

export default function ReportCard({
  post, cat, accentColor, isAdmin, user,
  onOpen, onEdit, onDelete, onToggleLike, onTogglePin, onToggleStar, isStarred,
  pinLoading, onAllowEdit, onShare, threads = [], C = {},
  postCol, portalId,
}) {
  const [hov,              setHov]          = useState(false);
  const [menuOpen,         setMenuOpen]      = useState(false);
  const [coverError,       setCoverError]    = useState(false);
  const [showSharePanel,   setShowSharePanel]   = useState(false);
  const [showExternalPanel, setShowExternal] = useState(false);
  const [showChatShare,    setShowChatShare]    = useState(false);

  const isAuthor = post.uploadedByUid === user?.uid || post.uploadedById === user?.uid;

  const accent     = cat?.color || accentColor || '#10b981';
  const isPinned   = !!(post.isPinned || post.pinned);
  const isApproved = !!post.adminLiked;
  const excerpt    = (post.sections||[]).find(s => s.type==='paragraph')?.text
                  || post.description || '';

  const attachmentCount = (post.sections||[]).filter(s => s.url && s.type !== 'paragraph' && s.type !== 'heading' && s.type !== 'bullets').length;

  // theme fallbacks
  const bg      = C.panel  || '#111827';
  const bg2     = C.panel2 || '#1a2030';
  const border  = C.border || '#1e2d3d';
  const fg      = C.bright || '#e2e8f0';
  const dim     = C.dim    || '#64748b';
  const mono    = C.mono   || 'monospace';
  const sans    = C.sans   || 'system-ui, sans-serif';

  const hoverBorder = hov ? accent + '55' : (isPinned ? '#4ade8040' : border);

  function handleClick(e) {
    if (menuOpen) return;
    onOpen?.(post);
  }

  const commentCount = post.commentCount ?? 0;
  const viewCount    = post.viewCount    ?? 0;
  const likeCount    = post.likeCount    ?? 0;

  return (
    <>
    <div
      onClick={handleClick}
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => { setHov(false); setMenuOpen(false); }}
      style={{
        background: bg,
        border: `1px solid ${hoverBorder}`,
        borderRadius: 12,
        overflow: 'hidden',
        cursor: 'pointer',
        transition: 'border-color 0.18s, box-shadow 0.18s',
        boxShadow: hov ? `0 4px 24px rgba(0,0,0,0.4)` : '0 1px 4px rgba(0,0,0,0.25)',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {/* ── Cover image ──────────────────────────────────────── */}
      {post.coverUrl && !coverError && (
        <div style={{ width:'100%', height:172, overflow:'hidden', flexShrink:0, position:'relative' }}>
          <img
            src={post.coverUrl}
            alt={post.title}
            onError={() => setCoverError(true)}
            style={{ width:'100%', height:'100%', objectFit:'cover', display:'block' }}
          />
          {/* Subtle gradient overlay at bottom for readability */}
          <div style={{
            position:'absolute', bottom:0, left:0, right:0, height:48,
            background:'linear-gradient(to top, rgba(0,0,0,0.55), transparent)',
          }} />
          {/* Status dot in top-left corner */}
          {isPinned && (
            <div style={{
              position:'absolute', top:10, left:10,
              fontFamily:mono, fontSize:9, letterSpacing:'0.12em',
              padding:'3px 8px', borderRadius:20,
              background:'rgba(74,222,128,0.85)', color:'#022c22',
              fontWeight:700,
            }}>WORKBENCH</div>
          )}
        </div>
      )}

      {/* ── Content area ─────────────────────────────────────── */}
      <div style={{ padding: post.coverUrl ? '14px 16px 0' : '16px 16px 0', flex:1, display:'flex', flexDirection:'column', gap:10 }}>

        {/* Row 1: category pill + flags */}
        <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', gap:8, flexWrap:'wrap' }}>
          <div style={{ display:'flex', alignItems:'center', gap:6, flexWrap:'wrap' }}>
            {/* Category pill */}
            <span style={{
              display:'inline-flex', alignItems:'center', gap:5,
              padding:'4px 10px', borderRadius:20,
              background: accent + '18',
              border: `1px solid ${accent}35`,
              fontFamily:mono, fontSize:9, letterSpacing:'0.08em',
              color: accent, fontWeight:600, whiteSpace:'nowrap',
            }}>
              {cat?.icon && <span style={{ fontSize:10 }}>{cat.icon}</span>}
              {cat?.label || 'REPORT'}
            </span>

            {/* Featured badge */}
            {post.isFeatured && (
              <span style={{
                fontFamily:mono, fontSize:8.5, letterSpacing:'0.12em',
                padding:'3px 7px', borderRadius:20,
                background:'rgba(186,117,23,0.15)',
                border:'1px solid rgba(186,117,23,0.30)',
                color:'#BA7517', fontWeight:600,
              }}>◆ FEATURED</span>
            )}

            {/* Approved badge */}
            {isApproved ? (
              <span style={{
                fontFamily:mono, fontSize:8.5, letterSpacing:'0.12em',
                padding:'3px 7px', borderRadius:20,
                background:'rgba(29,158,117,0.12)',
                border:'1px solid rgba(29,158,117,0.25)',
                color:'#1D9E75', fontWeight:600,
              }}>
                ✓ APPROVED{post.adminApprovedBy ? ` · ${post.adminApprovedBy}` : ''}
              </span>
            ) : (post.uploadedByUid === user?.uid || post.uploadedById === user?.uid) ? (
              <span style={{
                fontFamily:mono, fontSize:8.5, letterSpacing:'0.12em',
                padding:'3px 7px', borderRadius:20,
                background:'rgba(186,117,23,0.10)',
                border:'1px solid rgba(186,117,23,0.20)',
                color:'#BA7517',
              }}>⏳ PENDING APPROVAL</span>
            ) : null}

            {/* Cross-team badge */}
            {post._crossTeam && (
              <span style={{
                display:'inline-flex', alignItems:'center', gap:4,
                padding:'3px 8px', borderRadius:20,
                background: (post._fromPortalColor||'#10b981') + '18',
                border: `1px solid ${(post._fromPortalColor||'#10b981')}35`,
                fontFamily:mono, fontSize:8.5, letterSpacing:'0.08em',
                color: post._fromPortalColor||'#10b981', whiteSpace:'nowrap',
              }}>
                <span style={{ width:5, height:5, borderRadius:'50%', background:post._fromPortalColor||'#10b981', display:'block' }} />
                {post._shareType === 'share' ? '→ FROM' : '⟺ WITH'} {post._fromPortalLabel?.toUpperCase()}
              </span>
            )}

            {/* Shared-out badge */}
            {!post._crossTeam && post.isShared && (post.sharedWithPortals||[]).length > 0 && (
              <span style={{
                fontFamily:mono, fontSize:8.5, letterSpacing:'0.08em',
                padding:'3px 7px', borderRadius:20,
                background: border + '60', border: `1px solid ${border}`,
                color: dim, whiteSpace:'nowrap',
              }}>
                ↗ {post.sharedWithPortals.length} TEAM{post.sharedWithPortals.length > 1 ? 'S' : ''}
              </span>
            )}
          </div>

          {/* Visibility badges */}
          <div style={{ display:'flex', gap:4, alignItems:'center' }}>
            {post.visibility === 'restricted' && (
              <span style={{ fontSize:9, padding:'2px 6px', borderRadius:4, fontFamily:mono, background:'rgba(186,117,23,0.12)', color:'#BA7517', border:'0.5px solid rgba(186,117,23,0.25)' }}>
                👥 {(post.restrictedToUids?.length || 0) + 1} members
              </span>
            )}
            {post.visibility === 'private' && (
              <span style={{ fontSize:9, padding:'2px 6px', borderRadius:4, fontFamily:mono, background:'rgba(255,255,255,0.06)', color:dim }}>
                🔒 Private
              </span>
            )}
            {post.externalShared && (
              <span style={{ fontSize:9, padding:'2px 6px', borderRadius:4, fontFamily:mono, background:'rgba(127,119,221,0.12)', color:'#7F77DD', border:'0.5px solid rgba(127,119,221,0.25)' }}>
                🔗 External
              </span>
            )}
            {post.visibility === 'unlisted' && (
              <span style={{ fontFamily:mono, fontSize:8, color:'#f59e0b' }}>👁</span>
            )}
          </div>
        </div>

        <InternApprovalNote post={post} compact />

        {/* Row 2: report ID + title */}
        <span className="smc2-record-id" title="Report ID" style={{ marginBottom:-6 }}>
          {reportId(post, portalId || post._portalId)}
        </span>
        <h3 style={{
          margin:0, fontSize:15, fontWeight:700, color:fg,
          lineHeight:1.35, letterSpacing:'-0.01em',
        }}>
          {post.title}
        </h3>

        {/* Row 3: excerpt */}
        {excerpt ? (
          <p style={{
            margin:0, fontSize:12.5, color:dim, lineHeight:1.6,
            display:'-webkit-box', WebkitLineClamp:2,
            WebkitBoxOrient:'vertical', overflow:'hidden',
          }}>
            {excerpt}
          </p>
        ) : null}

        {/* Row 4: thread previews (compact) */}
        {threads.length > 0 && (
          <div style={{ display:'flex', gap:5, flexWrap:'wrap' }}>
            {threads.slice(0, 3).map(t => (
              <div key={t.id}
                onClick={e => { e.stopPropagation(); onOpen?.(t); }}
                style={{
                  display:'flex', alignItems:'center', gap:5,
                  padding:'4px 9px', borderRadius:6,
                  background: bg2, border:`1px solid ${border}`,
                  fontSize:11, color:dim, cursor:'pointer',
                  maxWidth:180, overflow:'hidden',
                }}>
                <span style={{ color:accent, fontSize:10 }}>↳</span>
                <span style={{ overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap', fontFamily:sans }}>
                  {t.title}
                </span>
              </div>
            ))}
            {threads.length > 3 && (
              <div
                onClick={e => { e.stopPropagation(); onOpen?.(post); }}
                style={{ padding:'4px 9px', borderRadius:6, background:accent+'18', border:`1px solid ${accent}35`,
                  fontSize:10, color:accent, fontFamily:mono, cursor:'pointer' }}
                title={`View all ${threads.length} threads`}
              >
                +{threads.length - 3} more
              </div>
            )}
          </div>
        )}

        <div style={{ flex:1 }} />
      </div>

      {/* ── Footer ───────────────────────────────────────────── */}
      <div style={{
        display:'flex', alignItems:'center', justifyContent:'space-between',
        padding:'10px 16px',
        borderTop: `1px solid ${border}`,
        marginTop: 10,
        gap:8,
      }}>
        {/* Left: stats + approval status */}
        <div style={{ display:'flex', alignItems:'center', gap:10, flexWrap:'wrap' }}>
          {commentCount > 0 && (
            <span style={{ display:'flex', alignItems:'center', gap:4, fontFamily:mono, fontSize:10, color:dim }}>
              <svg width="13" height="13" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.7">
                <path d="M10 2C5.6 2 2 5.1 2 9c0 1.9.8 3.6 2.1 4.9L3 17l3.6-1.2C7.6 16.6 8.8 17 10 17c4.4 0 8-3.1 8-7s-3.6-7-8-7z"/>
              </svg>
              {commentCount}
            </span>
          )}
          {viewCount > 0 && (
            <span style={{ display:'flex', alignItems:'center', gap:4, fontFamily:mono, fontSize:10, color:dim }}>
              <svg width="13" height="13" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.7">
                <path d="M1 10s3.5-7 9-7 9 7 9 7-3.5 7-9 7-9-7-9-7z"/>
                <circle cx="10" cy="10" r="3"/>
              </svg>
              {viewCount}
            </span>
          )}
          {attachmentCount > 0 && (
            <span style={{ display:'flex', alignItems:'center', gap:4, fontFamily:mono, fontSize:10, color:dim }}>
              <svg width="12" height="12" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.7">
                <path d="M14 2H6a2 2 0 00-2 2v12a2 2 0 002 2h8a2 2 0 002-2V4a2 2 0 00-2-2z"/>
                <path d="M12 2v4h2"/>
              </svg>
              {attachmentCount}
            </span>
          )}
          {/* Approval indicator — visible to all users */}
          <span style={{
            fontFamily:mono, fontSize:8.5, padding:'2px 6px', borderRadius:4,
            background: isApproved ? 'rgba(29,158,117,0.10)' : 'rgba(255,255,255,0.04)',
            border: `0.5px solid ${isApproved ? 'rgba(29,158,117,0.25)' : border}`,
            color: isApproved ? '#1D9E75' : dim,
          }}>
            {isApproved ? '✓ APPROVED' : 'PENDING'}
          </span>
        </div>

        {/* Right: author + date + actions */}
        <div style={{ display:'flex', alignItems:'center', gap:8 }}>
          {/* Author avatar */}
          <div style={{
            width:26, height:26, borderRadius:'50%',
            background: accent + '28',
            border: `1.5px solid ${accent}45`,
            display:'grid', placeItems:'center',
            fontFamily:mono, fontSize:9.5, color:accent, fontWeight:700, flexShrink:0,
          }}>
            {initials(post.uploadedBy)}
          </div>
          {/* Date */}
          <span style={{ fontFamily:mono, fontSize:9.5, color:dim, whiteSpace:'nowrap' }}>
            {relTime(post.uploadedAt)}
          </span>

          {/* Approve toggle (admin) */}
          {isAdmin && (
            <button
              onClick={e => { e.stopPropagation(); onToggleLike?.(post); }}
              style={{
                background:'none', border:'none', cursor:'pointer',
                display:'flex', alignItems:'center', gap:3, padding:'2px 4px',
                color: isApproved ? '#1D9E75' : dim, fontSize:13,
              }}
              title={isApproved ? 'Revoke approval' : 'Approve report'}
            >
              {isApproved ? '✓' : '○'}
              {likeCount > 0 && <span style={{ fontFamily:mono, fontSize:9 }}>{likeCount}</span>}
            </button>
          )}

          {/* Star */}
          {onToggleStar && (
            <div onClick={e => e.stopPropagation()}>
              <StarButton
                post={post}
                isStarred={isStarred}
                onToggle={p => onToggleStar(p, accent)}
                size={13}
              />
            </div>
          )}

          {/* Share: copy link · send in chat · copy report ID */}
          <div onClick={e => e.stopPropagation()}>
            <ReportShareButton post={post} portalId={portalId} portalColor={accentColor}
              currentUser={user} color={dim} />
          </div>

          {/* Pin */}
          <div onClick={e => e.stopPropagation()}>
            <PinButton
              post={post}
              onToggle={p => onTogglePin?.(p)}
              loading={pinLoading}
              canPin={!post._crossTeam && (isAdmin || post.uploadedByUid === user?.uid)}
              size={13}
            />
          </div>

          {/* Admin kebab menu */}
          {isAdmin && (
            <div style={{ position:'relative' }} onClick={e => e.stopPropagation()}>
              <button
                onClick={e => { e.stopPropagation(); setMenuOpen(m => !m); }}
                style={{
                  background:'none', border:'none', cursor:'pointer',
                  color:dim, fontSize:17, padding:'0 3px', lineHeight:1,
                }}
              >⋮</button>
              {menuOpen && (
                <div style={{
                  position:'absolute', right:0, bottom:30, zIndex:30,
                  background:bg, border:`1px solid ${border}`,
                  borderRadius:8, minWidth:150,
                  boxShadow:'0 8px 24px rgba(0,0,0,0.5)',
                  overflow:'hidden',
                }}>
                  {[
                    { label:'✏ Edit',       color:'#818cf8', action:() => onEdit?.(post) },
                    ...(onAllowEdit && post.uploadedByUid && post.uploadedByUid !== user?.uid
                      ? [{ label:'👤 Allow Edit', color:'#4ade80', action:() => onAllowEdit?.(post) }]
                      : []
                    ),
                    {
                      label: post.isFeatured ? '◆ Unfeature' : '◆ Feature',
                      color: '#BA7517',
                      action: async () => {
                        const db2 = getFirestore(getApp());
                        const col = post._collection || 'system_posts';
                        await updateDoc(doc(db2, col, post.id), { isFeatured: !post.isFeatured });
                      },
                    },
                    { label:'💬 Share via Chat', color:'#6366f1', action:() => setShowChatShare(true) },
                    ...(!post._crossTeam ? [
                      { label:'👥 Share within portal', color:'#0e9cc0', action:() => setShowSharePanel(true) },
                      ...(['admin','ops'].includes(user?.role)
                        ? [{ label:'🔗 Share externally', color:'#7F77DD', action:() => setShowExternal(true) }]
                        : []
                      ),
                    ] : []),
                    { label:'🗑 Delete',     color:'#f87171', action:() => onDelete?.(post) },
                  ].map((item, i) => (
                    <button key={i} onClick={() => { setMenuOpen(false); item.action(); }}
                      style={{
                        display:'block', width:'100%', textAlign:'left',
                        padding:'10px 14px', background:'none', border:'none',
                        cursor:'pointer', color:item.color,
                        fontFamily:mono, fontSize:10, letterSpacing:1,
                        borderTop: i > 0 ? `1px solid ${border}` : 'none',
                      }}>
                      {item.label}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Non-admin author share control */}
          {!isAdmin && isAuthor && !post._crossTeam && postCol && (
            <button
              onClick={e => { e.stopPropagation(); setShowSharePanel(true); }}
              title="Sharing settings"
              style={{ background:'none', border:'none', cursor:'pointer', color:dim, fontSize:10, padding:'2px 6px', fontFamily:mono, letterSpacing:'0.05em' }}
            >Share</button>
          )}

          {/* Non-admin one-time edit */}
          {!isAdmin && onEdit && user && (post.editCount?.[user.uid] || 0) < 1 && (
            <button
              onClick={e => {
                e.stopPropagation();
                if (window.confirm('You can only edit this report once. Proceed?')) {
                  const clear = window.confirm('Clear existing content?\nOK = start fresh · Cancel = keep');
                  onEdit(post, clear);
                }
              }}
              title="Edit report (one-time)"
              style={{ background:'none', border:'none', cursor:'pointer', color:dim, fontSize:13, padding:'2px 4px' }}
            >✏</button>
          )}
        </div>
      </div>
    </div>

    {showSharePanel && postCol && portalId && (
      <WithinPortalSharePanel
        post={post}
        postCol={postCol}
        portalId={portalId}
        currentUser={user}
        onClose={() => setShowSharePanel(false)}
      />
    )}
    {showExternalPanel && postCol && (
      <ExternalSharePanel
        post={post}
        postCol={postCol}
        currentUser={user}
        onClose={() => setShowExternal(false)}
      />
    )}
    {showChatShare && (() => {
      const pc = PORTAL_CARDS.find(p => p.id === portalId);
      return (
        <ShareViaChatModal
          post={post}
          portalId={portalId}
          portalLabel={pc?.label || (portalId ? (portalId.charAt(0).toUpperCase() + portalId.slice(1)) : 'Report')}
          portalColor={pc?.color || accentColor || accent}
          portalPath={pc?.path || (portalId ? `/${portalId}` : '/')}
          currentUser={user}
          onClose={() => setShowChatShare(false)}
        />
      );
    })()}
    </>
  );
}
